import React, { useState, useRef, useEffect, useContext } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";

const URL_AI = process.env.REACT_APP_URL_AI;

const SALUDO =
  "¡Hola! Soy tu asistente Tydart con datos en tiempo real. Puedes preguntarme sobre ventas, compras, inventario, clientes, marketplaces, artículos top o cualquier dato del sistema.";

const HIDDEN_PATHS = ["/", "/Login", "/Logout"];

export default function AIChat() {
  const { user } = useContext(AuthContext);
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: SALUDO }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 120);
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");

    const newMessages = [...messages, { role: "user", content: text }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const history = newMessages.slice(1, -1);
      const { data } = await axios.post(
        `${URL_AI}/chat`,
        { message: text, history },
        { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } }
      );
      setMessages([...newMessages, { role: "assistant", content: data.reply }]);
    } catch (err) {
      console.error("AIChat error:", err?.response?.status, err?.response?.data, err?.message);
      const detail = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Error desconocido";
      setMessages([
        ...newMessages,
        { role: "assistant", content: `⚠️ Error: ${detail}` },
      ]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => setMessages([{ role: "assistant", content: SALUDO }]);

  // Solo mostrar si el usuario está logueado (tiene algún permiso de menú)
  if (!user || HIDDEN_PATHS.includes(pathname)) return null;
  if (!user.menu_reportes && !user.menu_admin && !user.menu_ventas) return null;

  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999 }}>
      {open && (
        <div style={{
          position: "absolute",
          bottom: 72,
          right: 0,
          width: 370,
          maxHeight: 540,
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid #e2e8f0",
          animation: "aiSlideUp 0.22s ease",
        }}>
          {/* Header */}
          <div style={{
            padding: "13px 16px",
            background: "linear-gradient(135deg, #0f172a 0%, #1e40af 100%)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "rgba(255,255,255,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <i className="fas fa-robot" style={{ fontSize: 18 }}></i>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: "0.88rem", lineHeight: 1.2 }}>
                Asistente Tydart
              </div>
              <div style={{ fontSize: "0.67rem", opacity: 0.72 }}>
                Datos en tiempo real
              </div>
            </div>
            <button
              onClick={clearChat}
              title="Nueva conversación"
              style={{
                background: "none", border: "none", color: "#fff",
                cursor: "pointer", opacity: 0.7, fontSize: "0.85rem",
                padding: "4px 6px", borderRadius: 6,
                transition: "opacity 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.7)}
            >
              <i className="fas fa-trash"></i>
            </button>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: "none", border: "none", color: "#fff",
                cursor: "pointer", fontSize: "1rem", opacity: 0.8,
                padding: "4px 6px", lineHeight: 1, borderRadius: 6,
              }}
            >
              ✕
            </button>
          </div>

          {/* Mensajes */}
          <div style={{
            flex: 1,
            overflowY: "auto",
            padding: "14px 14px 6px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            background: "#f8fafc",
          }}>
            {messages.map((m, i) => (
              <div key={i} style={{
                display: "flex",
                justifyContent: m.role === "user" ? "flex-end" : "flex-start",
              }}>
                {m.role === "assistant" && (
                  <div style={{
                    width: 26, height: 26, borderRadius: "50%",
                    background: "#1e40af",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, marginRight: 7, alignSelf: "flex-end",
                    color: "#fff", fontSize: 11,
                  }}>
                    <i className="fas fa-robot"></i>
                  </div>
                )}
                <div style={{
                  maxWidth: "78%",
                  padding: "9px 13px",
                  borderRadius: m.role === "user"
                    ? "14px 14px 3px 14px"
                    : "14px 14px 14px 3px",
                  background: m.role === "user" ? "#1e40af" : "#fff",
                  color: m.role === "user" ? "#fff" : "#1e293b",
                  fontSize: "0.79rem",
                  lineHeight: 1.55,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                  whiteSpace: "pre-wrap",
                }}>
                  {m.content}
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-end", gap: 7 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: "50%",
                  background: "#1e40af",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, color: "#fff", fontSize: 11,
                }}>
                  <i className="fas fa-robot"></i>
                </div>
                <div style={{
                  padding: "10px 16px", borderRadius: "14px 14px 14px 3px",
                  background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                  display: "flex", gap: 5, alignItems: "center",
                }}>
                  {[0, 1, 2].map((d) => (
                    <span key={d} style={{
                      width: 7, height: 7, borderRadius: "50%",
                      background: "#94a3b8", display: "inline-block",
                      animation: `aiDot 1.2s ease-in-out ${d * 0.2}s infinite`,
                    }} />
                  ))}
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: "10px 12px",
            borderTop: "1px solid #e2e8f0",
            background: "#fff",
            display: "flex",
            gap: 8,
            alignItems: "flex-end",
            flexShrink: 0,
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Pregunta algo sobre el negocio…"
              rows={1}
              style={{
                flex: 1,
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: "0.79rem",
                resize: "none",
                outline: "none",
                fontFamily: "inherit",
                lineHeight: 1.45,
                maxHeight: 80,
                overflowY: "auto",
                transition: "border-color 0.15s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#1e40af")}
              onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                border: "none",
                background: input.trim() && !loading ? "#1e40af" : "#e2e8f0",
                color: input.trim() && !loading ? "#fff" : "#94a3b8",
                cursor: input.trim() && !loading ? "pointer" : "default",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "background 0.2s",
                fontSize: 15,
              }}
            >
              <i className="fas fa-paper-plane"></i>
            </button>
          </div>
        </div>
      )}

      {/* Botón flotante */}
      <button
        onClick={() => setOpen((p) => !p)}
        title="Asistente IA"
        style={{
          width: 58,
          height: 58,
          borderRadius: "50%",
          border: "none",
          background: "linear-gradient(135deg, #0f172a 0%, #1e40af 100%)",
          color: "#fff",
          boxShadow: "0 4px 18px rgba(30,64,175,0.50)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.35rem",
          transition: "transform 0.2s, box-shadow 0.2s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.08)";
          e.currentTarget.style.boxShadow = "0 6px 24px rgba(30,64,175,0.60)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow = "0 4px 18px rgba(30,64,175,0.50)";
        }}
      >
        <i className={open ? "fas fa-times" : "fas fa-robot"}></i>
      </button>

      <style>{`
        @keyframes aiDot {
          0%, 80%, 100% { transform: scale(0.55); opacity: 0.35; }
          40%            { transform: scale(1);    opacity: 1;    }
        }
        @keyframes aiSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
      `}</style>
    </div>
  );
}
